/**
 * Drawing the elements onto a 2D context.
 *
 * Separate from the component for one reason: the PNG export draws the same
 * elements through the same function onto an off-screen context, so what is
 * saved is what was on screen rather than a second implementation of it.
 */

import type { BoardElement } from '../../lib/board/types';
import { elementBounds, smoothCurves, type Rect, type View } from './geometry';

/** Text is drawn at a fixed size in world units, so it zooms with everything
 *  else. `BoardElement` has no font size and inventing a field the server would
 *  drop is worse than one size. */
export const TEXT_SIZE = 18;
export const TEXT_LINE = 1.35;
export const TEXT_FONT = `${TEXT_SIZE}px Inter, system-ui, sans-serif`;

const ARROW_HEAD = 14;

export interface DrawOptions {
  view: View;
  /** CSS pixels. */
  width: number;
  height: number;
  background: string;
  /** A faint dot every 40 world units, so panning an empty board shows it
   *  moving. Off for the export, where there is nothing to orient. */
  grid?: string;
  selectedId?: string | null;
  selectionColor?: string;
}

export function draw(
  ctx: CanvasRenderingContext2D,
  elements: BoardElement[],
  opts: DrawOptions
): void {
  const { view } = opts;
  ctx.save();
  ctx.fillStyle = opts.background;
  ctx.fillRect(0, 0, opts.width, opts.height);

  if (opts.grid) drawGrid(ctx, opts);

  ctx.translate(-view.x * view.scale, -view.y * view.scale);
  ctx.scale(view.scale, view.scale);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  for (const el of elements) drawElement(ctx, el);

  if (opts.selectedId) {
    const selected = elements.find((e) => e.id === opts.selectedId);
    if (selected) drawSelection(ctx, elementBounds(selected), view.scale, opts.selectionColor);
  }

  ctx.restore();
}

function drawGrid(ctx: CanvasRenderingContext2D, opts: DrawOptions): void {
  const { view } = opts;
  const step = 40 * view.scale;
  if (step < 12) return;

  const offsetX = -((view.x * view.scale) % step);
  const offsetY = -((view.y * view.scale) % step);
  ctx.fillStyle = opts.grid!;
  for (let x = offsetX; x < opts.width; x += step) {
    for (let y = offsetY; y < opts.height; y += step) {
      ctx.fillRect(x, y, 1, 1);
    }
  }
}

export function drawElement(ctx: CanvasRenderingContext2D, el: BoardElement): void {
  ctx.strokeStyle = el.color;
  ctx.lineWidth = Math.max(el.width, 0.5);

  switch (el.type) {
    case 'stroke': {
      const path = smoothCurves(el.points ?? []);
      if (!path) return;
      ctx.beginPath();
      ctx.moveTo(el.x + path.start[0], el.y + path.start[1]);
      for (const c of path.curves) {
        ctx.quadraticCurveTo(el.x + c.cx, el.y + c.cy, el.x + c.x, el.y + c.y);
      }
      ctx.stroke();
      return;
    }
    case 'rect': {
      const b = elementBounds(el);
      const radius = Math.min(12, b.w / 2, b.h / 2);
      ctx.beginPath();
      ctx.roundRect(b.x, b.y, b.w, b.h, Math.max(radius, 0));
      if (el.fill) {
        ctx.fillStyle = el.fill;
        ctx.fill();
      }
      ctx.stroke();
      return;
    }
    case 'ellipse': {
      const b = elementBounds(el);
      ctx.beginPath();
      ctx.ellipse(b.x + b.w / 2, b.y + b.h / 2, b.w / 2, b.h / 2, 0, 0, Math.PI * 2);
      if (el.fill) {
        ctx.fillStyle = el.fill;
        ctx.fill();
      }
      ctx.stroke();
      return;
    }
    case 'line':
    case 'arrow': {
      const x2 = el.x + el.w;
      const y2 = el.y + el.h;
      ctx.beginPath();
      ctx.moveTo(el.x, el.y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      if (el.type === 'arrow') drawHead(ctx, el.x, el.y, x2, y2);
      return;
    }
    case 'text': {
      ctx.fillStyle = el.color;
      ctx.font = TEXT_FONT;
      ctx.textBaseline = 'top';
      const lines = (el.text ?? '').split('\n');
      lines.forEach((line, i) => {
        ctx.fillText(line, el.x, el.y + i * TEXT_SIZE * TEXT_LINE);
      });
      return;
    }
  }
}

function drawHead(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): void {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const spread = Math.PI / 7;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - ARROW_HEAD * Math.cos(angle - spread), y2 - ARROW_HEAD * Math.sin(angle - spread));
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - ARROW_HEAD * Math.cos(angle + spread), y2 - ARROW_HEAD * Math.sin(angle + spread));
  ctx.stroke();
}

function drawSelection(
  ctx: CanvasRenderingContext2D,
  box: Rect,
  scale: number,
  color = '#796cbf'
): void {
  const pad = 6 / scale;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1 / scale;
  ctx.setLineDash([4 / scale, 4 / scale]);
  ctx.strokeRect(box.x - pad, box.y - pad, box.w + pad * 2, box.h + pad * 2);
  ctx.restore();
}
